// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Data
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  RadioGroup,
  Radio,
  Box,
  CircularProgress,
} from "@mui/material";

import { useCallback, useContext, useEffect, useState } from "react";

import { LoginFormContext } from "context/loginFormCtx";
import MDInput from "components/MDInput";
import { fetchMasterSettingsApi } from "lib/Api/MasterSettings";
import { useFormik, Form, Formik, useFormikContext } from "formik";
import { Cancel, CancelOutlined, Check, CheckCircleOutline, Save } from "@mui/icons-material";
import { updateMasterSettingsApi } from "lib/Api/MasterSettings";

const BTN_ACTIONS = {
  SAVE: "save",
  CANCEL: "cancel",
};

function MasterSettings() {
  const { setNotification } = useContext(LoginFormContext);

  const [masterSettingConfiguration, setMasterSettingConfiguration] = useState([]);
  const [changedValues, setChangedValues] = useState([]);
  const [loadingStates, setLoadingStates] = useState({}); // Track loading state for each field
  const [isLoading, setIsLoading] = useState(false);

  // fetch master settings data from API
  const fetchMasterSetting = async () => {
    try {
      setIsLoading(true);
      const response = await fetchMasterSettingsApi();

      if (response?.status === 200) {
        const data = response.data?.data.rows;
        setMasterSettingConfiguration(data);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Something went wrong",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error?.response?.data?.message || "Something went wrong",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      primary_text_for_case_details: "0",
      persist_search: "0",
      payment_reminder_one: "0",
      payment_reminder_two: "0",
      payment_reminder_three: "0",
      trial_period_reminder: "0",  
    },
  });

  const { setFieldValue } = formik;

  const watchAllFields = formik.values;

  const handleSetValueInForm = () => {
    masterSettingConfiguration?.forEach((setting) =>
      setting?.masterSettings?.forEach((masterSetting) => {
        if (masterSetting?.name === "appointment_cancellation_charges") {
          setFieldValue("appointment_cancellation_charges", masterSetting?.value === "1");
        } else {
          setFieldValue(`${masterSetting?.name}`, masterSetting?.value);
        }
      })
    );
  };

  const handleValueChange = (fieldName) => {
    if (!changedValues?.includes(fieldName)) setChangedValues([...changedValues, fieldName]);
  };

  const handleActionOnSetting = async (action, fieldName, valueIfCancel, idForSave) => {
    if (action === BTN_ACTIONS.SAVE) {
      // Check if already loading
      if (loadingStates[fieldName]) return;

      // Set loading state for this field
      setLoadingStates((prev) => ({ ...prev, [fieldName]: true }));

      let data = {
        name: fieldName,
        value: watchAllFields?.[fieldName],
      };

      if (fieldName === "appointment_cancellation_charges") {
        data = {
          name: fieldName,
          value: watchAllFields?.[fieldName] ? "1" : "0",
        };
      }

      try {
        const response = await updateMasterSettingsApi(idForSave, data);
        if (response?.status === 200) {
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: "Master setting configuration updated successfully",
            open: true,
          });

          setChangedValues(changedValues?.filter((value) => value !== fieldName));
        } else {
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: response?.data?.message || "Something went wrong",
            open: true,
          });
        }
      } catch (error) {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: error?.response?.data?.message || "Something went wrong",
          open: true,
        });
      } finally {
        // Clear loading state
        setLoadingStates((prev) => ({ ...prev, [fieldName]: false }));
      }
    } else if (action === BTN_ACTIONS.CANCEL) {
      setFieldValue(fieldName, valueIfCancel);
      setChangedValues(changedValues?.filter((value) => value !== fieldName));
    }
  };

  useEffect(() => {
    fetchMasterSetting();
  }, []);

  useEffect(() => {
    if (masterSettingConfiguration?.length > 0) {
      handleSetValueInForm();
    }
  }, [masterSettingConfiguration]);

  return (
    <CustomPageLayout>
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <MDTypography variant="h5" fontWeight="medium" mb={1}>
              Master Settings
            </MDTypography>
            <MDTypography variant="button" color="text" mb={3} display="block">
              Here you can manage all the master settings.
            </MDTypography>

            {isLoading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress size={50} />
              </MDBox>
            ) : (
              masterSettingConfiguration.map(
              (setting) =>
                setting?.masterSettings?.length > 0 && (
                  <Paper key={setting.id} sx={{ mb: 2, p: 3 }}>
                    <Typography variant="h5" sx={{ color: "#344767", mb: 3, fontWeight: "bold" }}>
                      {setting?.name}
                    </Typography>

                    <Box component="form">
                      {[...setting.masterSettings].map((masterSetting) => (
                        <Box
                          key={masterSetting.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2.5,
                            gap: 2,
                          }}
                        >
                          <Grid container alignItems="center">
                            <Grid item xs={12} md={3}>
                              <Typography variant="body1" sx={{ color: "#344767" }}>
                                {masterSetting?.label} :
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={3}>
                              {masterSetting?.name === "primary_text_for_case_details" ? (
                                <RadioGroup
                                  row
                                  name={masterSetting?.name}
                                  value={formik.values?.[masterSetting?.name]}
                                  onChange={(event) => {
                                    setFieldValue(masterSetting?.name, event.target.value);
                                    handleValueChange(masterSetting?.name);
                                  }}
                                >
                                  <FormControlLabel
                                    value="0"
                                    control={
                                      <Radio
                                        sx={{
                                          "&.Mui-checked": {
                                            color: "#2196f3",
                                          },
                                        }}
                                      />
                                    }
                                    label="Case ID"
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={
                                      <Radio
                                        sx={{
                                          "&.Mui-checked": {
                                            color: "#2196f3",
                                          },
                                        }}
                                      />
                                    }
                                    label="Identification Number"
                                  />
                                </RadioGroup>
                              ) : masterSetting?.name === "persist_search" ? (
                                <RadioGroup
                                  row
                                  name={masterSetting?.name}
                                  value={formik.values?.[masterSetting?.name]}
                                  onChange={(event) => {
                                    setFieldValue(masterSetting?.name, event.target.value);
                                    handleValueChange(masterSetting?.name);
                                  }}
                                >
                                  <FormControlLabel
                                    value="1"
                                    control={
                                      <Radio
                                        sx={{
                                          "&.Mui-checked": {
                                            color: "#2196f3",
                                          },
                                        }}
                                      />
                                    }
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="0"
                                    control={
                                      <Radio
                                        sx={{
                                          "&.Mui-checked": {
                                            color: "#2196f3",
                                          },
                                        }}
                                      />
                                    }
                                    label="No"
                                  />
                                </RadioGroup>
                              ) : masterSetting?.name === "appointment_cancellation_charges" ? (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={formik.values?.[masterSetting?.name] === "1"}
                                      onChange={(event) => {
                                        setFieldValue(
                                          masterSetting?.name,
                                          event.target.checked ? "1" : "0"
                                        );
                                        handleValueChange(masterSetting?.name);
                                      }}
                                      sx={{
                                        "&.Mui-checked": {
                                          color: "#2196f3",
                                        },
                                      }}
                                    />
                                  }
                                  label={masterSetting?.label}
                                />
                              ) : (
                                <MDInput
                                  type="number"
                                  name={masterSetting?.name}
                                  value={formik.values?.[masterSetting?.name]}
                                  onChange={(event) => {
                                    const value = event.target.value.replace(/[^0-9]/g, "");
                                    setFieldValue(masterSetting?.name, value < 1 ? "1" : value);
                                    handleValueChange(masterSetting?.name);
                                  }}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                      height: "40px",
                                    },
                                  }}
                                />
                              )}
                            </Grid>

                            <Grid item xs={12} md={2}>
                              {changedValues?.includes(masterSetting?.name) && (
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <IconButton
                                    size="small"
                                    disabled={loadingStates[masterSetting?.name]}
                                    sx={{
                                      color: "#4CAF50",
                                      "&:hover": {
                                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                                      },
                                    }}
                                    onClick={() => {
                                      handleActionOnSetting(
                                        BTN_ACTIONS.SAVE,
                                        masterSetting?.name,
                                        null,
                                        masterSetting?.id
                                      );
                                    }}
                                  >
                                    {loadingStates[masterSetting?.name] ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      <CheckCircleOutline />
                                    )}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    disabled={loadingStates[masterSetting?.name]}
                                    sx={{
                                      color: "#F44335",
                                      "&:hover": {
                                        backgroundColor: "rgba(244, 67, 53, 0.1)",
                                      },
                                    }}
                                    onClick={() => {
                                      handleActionOnSetting(
                                        BTN_ACTIONS.CANCEL,
                                        masterSetting?.name,
                                        masterSetting?.value
                                      );
                                    }}
                                  >
                                    <CancelOutlined />
                                  </IconButton>
                                </Stack>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )
              )
            )}
          </MDBox>
        </Card>
      </MDBox>
    </CustomPageLayout>
  );
}

export default MasterSettings;
